import cn from "classnames";
import { useEffect } from "react";
import { useIntl } from "react-intl";

// Components
import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, FormControlLabel, Typography } from "@mui/material";
import { Checkbox } from "components/Checkbox";
import { CustomiseLabels } from "../components/CustomiseLabels";

// Hooks
import useBehaviouralBreakdown from "./useBehaviouralBreakdown";

// Highchart
import Highcharts from "highcharts";
import HC_more from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";

// Data
import { ChartCarousel } from "../components/ChartCarousel";
import { carouselData, carouselDataTitles } from "./BehaviouralBreakdown.data";

// Styles
import "assets/styles/components/highcharts.css";
import behaviouralBreakdownStyles from "./BehaviouralBreakdown.styles";

// Types
import { IBaseChartProps } from "components/Charts/chart.types";

HC_more(Highcharts);

const BehaviouralBreakdown = ({
    width,
    height,
    showDescription,
    getData,
    moreOptions,
}: IBaseChartProps): JSX.Element => {
    const intl = useIntl();
    const classes = behaviouralBreakdownStyles();

    const {
        chartOptions,
        chartRef,
        shouldExpandAll,
        hideExpandButtons,
        showDefinitions,
        paceCurrentSlide,
        triadCurrentSlide,
        filterBy,
        customiseLabels,
        setFilterBy,
        handleExpandAll,
        handleCheckExpandAll,
        handleCheckPaceDefinitions,
        handleSetCustomiseLabels,
    } = useBehaviouralBreakdown({
        width,
        height,
        moreOptions,
        getData,
    });

    useEffect(() => {
        if (showDefinitions && !moreOptions?.fallbackCondition) {
            handleExpandAll(showDefinitions);
        }
    }, [handleExpandAll, moreOptions, showDefinitions]);

    return (
        <>
            {showDescription && (
                <Typography paragraph sx={{ alignItems: "justified" }}>
                    {intl.formatMessage({ id: "charts.behaviouralBreakdown.intro" })}
                </Typography>
            )}
            <Box position="relative" sx={{ overflow: "hidden", width: "100%" }}>
                {moreOptions?.fallbackCondition && moreOptions?.fallbackComponent}

                <Box display="flex" justifyContent="flex-end" sx={{ width: "calc(100% - 112px)", mb: 1 }}>
                    {!hideExpandButtons && (
                        <FormControlLabel
                            sx={{
                                mr: 0,
                                "& .MuiTypography-root": {
                                    fontSize: 12,
                                    letterSpacing: 1.25,
                                },
                                "& .MuiCheckbox-root": {
                                    padding: 0,
                                    mr: 1,
                                },
                            }}
                            control={
                                <Checkbox
                                    disabled={showDefinitions}
                                    checked={shouldExpandAll}
                                    onChange={(ev) => handleCheckExpandAll(ev)}
                                />
                            }
                            label={intl.formatMessage({ id: "charts.behaviouralBreakdown.expandAll.label" })}
                        />
                    )}
                    {moreOptions?.shouldShowPaceDefinitions && (
                        <FormControlLabel
                            sx={{
                                ml: 2,
                                mr: 0,
                                "& .MuiTypography-root": {
                                    fontSize: 12,
                                    letterSpacing: 1.25,
                                },
                                "& .MuiCheckbox-root": {
                                    padding: 0,
                                    mr: 1,
                                },
                            }}
                            control={
                                <Checkbox checked={showDefinitions} onChange={(ev) => handleCheckPaceDefinitions(ev)} />
                            }
                            label={intl.formatMessage({ id: "charts.behaviouralBreakdown.showDefinitions.label" })}
                        />
                    )}
                </Box>

                <Box
                    className={cn([
                        classes.chartWrapper,
                        "BehaviouralBreakdown",
                        {
                            "BehaviouralBreakdown-show-definitions": showDefinitions,
                        },
                    ])}
                >
                    {showDefinitions && shouldExpandAll && (
                        <>
                            <Box className={cn([classes.infoWrapper, classes.infoWrapperTop])}>
                                <Box className={classes.introTitle}>
                                    <InfoIcon />
                                    <Typography variant="h3">
                                        {intl.formatMessage({ id: "charts.behaviouralBreakdown.paceDefinitions" })}
                                    </Typography>
                                </Box>
                                <Box className={classes.paceNavLinks}>
                                    {carouselDataTitles.map((el, i) => (
                                        <Button
                                            key={i}
                                            variant="text"
                                            onClick={() => setFilterBy(el.name)}
                                            sx={{
                                                color: el.color,
                                                borderBottomColor:
                                                    filterBy === el.name ? `1px solid ${el.color}` : "transparent",
                                            }}
                                        >
                                            {el.name[0].toUpperCase()}
                                        </Button>
                                    ))}
                                </Box>
                                <Box className={classes.bodyBlock}>
                                    {filterBy ? (
                                        <ChartCarousel
                                            slides={carouselData.filter((el) => el.parent === filterBy)}
                                            selectedSlide={paceCurrentSlide}
                                        />
                                    ) : (
                                        <>
                                            <Typography paragraph>
                                                <Typography component="strong">
                                                    {intl.formatMessage({
                                                        id: "charts.behaviouralBreakdown.paceDefinitions.intro1",
                                                    })}
                                                </Typography>
                                            </Typography>
                                            <Typography paragraph>
                                                {intl.formatMessage({
                                                    id: "charts.behaviouralBreakdown.paceDefinitions.intro2",
                                                })}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            <Box className={cn([classes.infoWrapper, classes.infoWrapperBottom])}>
                                <ChartCarousel
                                    selectedSlide={triadCurrentSlide}
                                    slides={carouselData.filter(
                                        (el) =>
                                            !carouselDataTitles
                                                .map((e) => e.name)
                                                .includes(el.parent as typeof carouselDataTitles[number]["name"])
                                    )}
                                />
                            </Box>
                        </>
                    )}

                    {!moreOptions?.fallbackCondition ? (
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                    ) : (
                        <Box className={classes.imagePlaceholder} />
                    )}
                </Box>

                {!moreOptions?.hideCustomizeLabels && (
                    <CustomiseLabels options={customiseLabels} setOptions={handleSetCustomiseLabels} />
                )}
            </Box>
        </>
    );
};

export default BehaviouralBreakdown;
